import React from 'react';

import { shallowEqual, useSelector } from 'react-redux';
import Feedbacks from 'screens/StrengthDetail/Feedbacks';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { Divider1, SpaceBetweenContainer, VerticallyCenterContainer } from 'styles/common';

import { FEEDBACK_STRENGTH_TYPES, SENTIMENTS_TYPES } from 'constants/ProjectConstants';
import { CloseIcon2, FeedbackSvgIcon, PraiseSvgIcon } from 'constants/icons';
import theme from 'constants/theme';

import { getAllSharedFeedbackListV1, getAllReceivedFeedbackListV1 } from 'services/continuousFeedback';

import { Typography13 } from 'uiToolkit/Typography';

import ClickToSetNavBar from './common/ClickToSetNavBar';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledClickToSetNavBar = styled(ClickToSetNavBar)`
  padding: 24px;
  > div:first-child {
    right: 0;
    padding-left: 24px;
  }
  > div:nth-child(2) {
    height: calc(100% - 2rem);
    top: 2rem;
    padding-right: 0;
  }
`;

const ItemsArray = [
  {
    id: 'appreciative',
    header: ({ isSelected }) => (
      <VerticallyCenterContainer>
        <PraiseSvgIcon containerSize="fixed12px" size="fixed12px" />
        <Typography13
          color={isSelected ? theme.palette.primary.main : 'textSecondary'}
          weight={isSelected ? 600 : 100}
          leftGutter={1}
        >
          {FEEDBACK_STRENGTH_TYPES.APPRECIATIVE}
        </Typography13>
      </VerticallyCenterContainer>
    ),
    component: ({ isShared, employeeId }) => {
      const getSharedKudosData = (limit, offset) =>
        getAllSharedFeedbackListV1({ limit, offset, feedbackType: 'KUDOS' });
      const getReceivedKudosData = (limit, offset) =>
        getAllReceivedFeedbackListV1({ limit, offset, feedbackType: 'KUDOS' });

      return (
        <Feedbacks
          headerText={isShared ? 'Praise(s) you have shared' : 'Praise(s) you have received'}
          type={SENTIMENTS_TYPES.appreciative.value}
          hasCount={false}
          noHorizontalPadding
          fetchDataFn={isShared ? getSharedKudosData : getReceivedKudosData}
          employeeId={employeeId}
        />
      );
    },
  },
  {
    id: 'constructive',
    header: ({ isSelected }) => (
      <VerticallyCenterContainer>
        <FeedbackSvgIcon containerSize="fixed12px" size="fixed12px" />
        <Typography13
          color={isSelected ? theme.palette.primary.main : 'textSecondary'}
          weight={isSelected ? 600 : 100}
          leftGutter={1}
        >
          {FEEDBACK_STRENGTH_TYPES.CONSTRUCTIVE}
        </Typography13>
      </VerticallyCenterContainer>
    ),
    component: ({ isShared, employeeId }) => {
      const getSharedFeedbacksData = (limit, offset) =>
        getAllSharedFeedbackListV1({ limit, offset, feedbackType: 'FEEDBACK' });
      const getReceivedFeedbacksData = (limit, offset) =>
        getAllReceivedFeedbackListV1({ limit, offset, feedbackType: 'FEEDBACK' });
      return (
        <Feedbacks
          headerText={isShared ? 'Advice you have shared' : 'Advice you have received'}
          hasCount={false}
          noHorizontalPadding
          type={SENTIMENTS_TYPES.constructive.value}
          fetchDataFn={isShared ? getSharedFeedbacksData : getReceivedFeedbacksData}
          employeeId={employeeId}
        />
      );
    },
  },
];

/** Shared feedback */
const ShowSharedOrReceivedFeedback = ({ handleClose, isShared }) => {
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId, shallowEqual);

  return (
    <Container>
      <SpaceBetweenContainer style={{ alignItems: 'center', padding: '24px', paddingBottom: '16px' }}>
        <Typography13 style={{ overflowWrap: 'break-word', width: '95%' }} weight={600}>
          {isShared ? 'All Shared Feedback' : 'All Received Feedback'}
        </Typography13>
        <CloseIcon2
          size="fixed12px"
          color={theme.palette.icons.blackv1}
          onClick={handleClose}
          style={{ marginRight: '-8px' }}
        />
      </SpaceBetweenContainer>
      <Divider1 bottomGutter={2} />
      <StyledClickToSetNavBar
        sectionList={ItemsArray.map(({ component, header, id }) => ({
          id,
          component: () => component({ isShared, employeeId: loggedInEmployeeId }),
          header: (isSelected) =>
            header({
              isSelected,
            }),
        }))}
      />
    </Container>
  );
};

export default ShowSharedOrReceivedFeedback;
